import { captureException } from "@sentry/react"
import { Button, Tag, toast, Typography } from "@suraasa/placebo-ui"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { LaunchPracticeInterviewResponse } from "api/resources/bePracticeInterview/types"
import { LearningItem } from "api/resources/learningItems/types"
import { useEffect, useState } from "react"
import Lottie from "react-lottie"

import { useLaunchPracticeInterview } from "../hooks/useLaunchPracticeInterview"
import TitleCard from "./TitleCard"

const AI_INTERVIEW_INSTRUCTIONS = [
  "The Mock Interview gives you a chance to rehearse real interview scenarios with an AI interviewer before you face the real thing.",
  "During the session, you'll be asked questions similar to what a real interview panel might ask, so you can practice thinking on your feet.",
  "Based on your responses, you receive a personalized feedback report, highlighting your strengths and where you can improve.",
  "This feedback helps you walk into your actual interviews more confident and prepared.",
  "You only get one attempt, so make sure you're ready before you begin.",
  "If you abandon the interview midway, it will restart from the beginning.",
]

const AiInterviewerCTA = ({
  result,
  isLoading,
  loadingAnimation,
  onStart,
  isStarting,
}: {
  result: LaunchPracticeInterviewResponse | undefined
  isLoading: boolean
  loadingAnimation: object | null
  onStart: () => void
  isStarting: boolean
}) => {
  const isGeneratingReport =
    result?.status === "complete" && result.reportStatus === "pending"

  const isCompleted = result?.status === "complete"
  const isReportUnavailable =
    isCompleted &&
    result?.reportStatus !== "pending" &&
    result?.reportStatus !== "ready"

  if (isLoading) return undefined

  if (isGeneratingReport) {
    return (
      <div className="flex items-center gap-1">
        {loadingAnimation ? (
          // @ts-expect-error react version issues
          <Lottie
            style={{
              cursor: "default",
              width: 24,
              height: 24,
              margin: 0,
            }}
            isClickToPauseDisabled
            options={{
              loop: true,
              autoplay: true,
              animationData: loadingAnimation,
            }}
          />
        ) : (
          <div className="size-3 animate-pulse rounded-full bg-interactive-400" />
        )}
        <Typography variant="strongSmallBody" className="text-interactive-900">
          Generating report. Hang tight!
        </Typography>
      </div>
    )
  }

  if (isCompleted) {
    if (result?.reportStatus === "ready" && result.reportUrl) {
      return (
        <Button
          color="black"
          onClick={() => window.open(result.reportUrl ?? undefined, "_blank")}
        >
          View Report
        </Button>
      )
    }

    return (
      <Tag color={isReportUnavailable ? "critical" : "success"}>
        {isReportUnavailable
          ? "Report unavailable"
          : "Report is being generated"}
      </Tag>
    )
  }

  return (
    <Button color="black" onClick={onStart} loading={isStarting}>
      {result?.invitationStatus === "started"
        ? "Continue Interview"
        : "Start Interview"}
    </Button>
  )
}

const AiInterviewer = ({ item }: { item: LearningItem }) => {
  if (item.learningContentType !== "ai_interview")
    throw new Error("Invalid content type passed to AiInterviewer component")

  const { data: result, isLoading } = useLaunchPracticeInterview(true)
  const queryClient = useQueryClient()

  const [loadingAnimation, setLoadingAnimation] = useState<object | null>(null)

  const isGeneratingReport =
    result?.status === "complete" && result.reportStatus === "pending"

  useEffect(() => {
    if (!isGeneratingReport || loadingAnimation) return
    import("assets/Cpat/cpat_loading_spinner.json").then(mod =>
      setLoadingAnimation(mod.default)
    )
  }, [isGeneratingReport, loadingAnimation])

  // Only this explicit, learner-initiated call asks for a fresh magic link -
  // the passive status query above always requests generateMagicLink=false,
  // so it never invalidates a link the learner may already have open.
  const startInterview = useMutation({
    mutationFn: () =>
      api.bePracticeInterview.launch({ data: { generateMagicLink: true } }),
    onSuccess: res => {
      if (!res.magicLink) {
        captureException(
          new Error("Practice interview launch returned no magic link"),
          scope => {
            scope.setTag("api", "bePracticeInterview.launch")
            scope.setExtras({
              status: res.status,
              invitationStatus: res.invitationStatus,
            })
            return scope
          }
        )
        toast.error("Unable to start interview", {
          description: "Please contact care@suraasa.com",
        })
        return
      }
      let magicLink = res.magicLink
      try {
        const url = new URL(magicLink)
        url.searchParams.set("redirect-url", window.location.href)
        magicLink = url.toString()
      } catch (error) {
        console.error("Unable to append redirect-url to magic link", error)
      }
      window.open(magicLink, "_blank")
      queryClient.invalidateQueries({
        queryKey: queries.bePracticeInterview.launch().queryKey,
      })
    },
    onError: () => {
      toast.error("Unable to start interview", {
        description: "Please contact care@suraasa.com",
      })
    },
  })

  return (
    <div className="mb-6">
      <TitleCard
        title={item.title}
        subtitle="Practice your interview skills with an AI interviewer"
        action={
          <AiInterviewerCTA
            result={result}
            isLoading={isLoading}
            loadingAnimation={loadingAnimation}
            onStart={() => startInterview.mutate()}
            isStarting={startInterview.isPending}
          />
        }
      />
      <Typography variant="title4" className="my-2">
        Instructions
      </Typography>
      <ul className="mb-10 list-disc space-y-1 pl-4">
        {AI_INTERVIEW_INSTRUCTIONS.map(point => (
          <li key={point}>
            <Typography>{point}</Typography>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default AiInterviewer
